import { useEffect } from 'react';

// No react-helmet-async in this project — declarative React Router mode
// (BrowserRouter + Routes, not the data router) has no built-in head
// management, so this sets document.title and upserts the description/
// canonical tags directly rather than adding a dependency for it.
import seo from '../seo-routes.json';

const CANONICAL_ORIGIN = seo.origin;
const ROUTES = Object.fromEntries(seo.routes.map((r) => [r.path, r]));

const upsertMeta = (name, content) => {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

// This is an SPA — meta tags set via JS persist across client-side
// navigation unless the next page explicitly changes them. A page that
// doesn't pass `robots` must actively clear any noindex tag a previous
// page left behind, not just skip setting one.
const setRobots = (content) => {
  const el = document.head.querySelector('meta[name="robots"]');
  if (content) {
    if (el) {
      el.setAttribute('content', content);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  } else if (el) {
    el.remove();
  }
};

// Title, description and canonical come from src/seo-routes.json when the path has an entry
// (the same file scripts/prerender-meta.js uses, so client and pre-rendered head match).
// Explicit `title` / `description` are only fallbacks for paths without an entry
// (e.g. a product added later).
export const useDocumentHead = ({ title, description, path, robots }) => {
  const route = path ? ROUTES[path] : undefined;
  const finalTitle = route ? route.title : title;
  const finalDescription = route ? route.description : description;
  const finalCanonical = route ? route.canonical : (path ? `${CANONICAL_ORIGIN}${path}` : undefined);
  useEffect(() => {
    if (finalTitle) document.title = finalTitle;
    if (finalDescription) upsertMeta('description', finalDescription);
    if (finalCanonical) upsertCanonical(finalCanonical);
    setRobots(robots);
  }, [finalTitle, finalDescription, finalCanonical, robots]);
};
